/**
 * ForgotPasswordPage — Vincent #191 (Eric 2026-05-28)
 *
 * Sans clé Resend opérationnelle, on ne peut pas envoyer un email de reset
 * direct. Au lieu de mentir à l'utilisateur (page "email envoyé" qui ne
 * fonctionne pas), on :
 *   1. Notifie les admin/CEO/président/VP de son org via la table notifications
 *   2. Affiche un message clair à l'utilisateur : "Demande enregistrée. Un
 *      administrateur de votre entreprise va vous contacter."
 *   3. Best-effort email Resend (no-op si pas de clé)
 *
 * L'admin verra la demande dans son badge notifications + dans Tier
 * Management (où il peut reset le mot de passe manuellement).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Envelope, ArrowLeft, SpinnerGap, CheckCircle } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { KNORIA_LOGO_URL } from "../components/KnoriaLogo";
import { useLang } from "../contexts/LanguageContext";
import { API_BASE as API } from "../lib/apiBase";

export default function ForgotPasswordPage() {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/auth/forgot-password`, { email }, { withCredentials: true });
      setEmailSent(!!r.data?.email_sent);
      setSubmitted(true);
    } catch {
      // Toujours afficher succès (anti-énumération d'emails). Privacy/Loi 25.
      setSubmitted(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 via-white to-blue-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-zinc-100 p-8" data-testid="forgot-password-page">
        <div className="text-center mb-6">
          <img src={KNORIA_LOGO_URL} alt="KNORIA" className="w-44 mx-auto" />
          <p className="text-xs text-zinc-500 mt-1">Knowledge Empowered</p>
        </div>

        {submitted ? (
          <div className="space-y-4" data-testid="forgot-password-success">
            <div className="text-center">
              <CheckCircle size={48} weight="fill" className="text-teal-500 mx-auto mb-2" />
              <h2 className="text-lg font-semibold text-zinc-800 mb-1">{lang === "en" ? "Request received" : "Demande enregistrée"}</h2>
            </div>
            <div className={`p-3 rounded-lg text-sm ${emailSent ? "bg-teal-50 border border-teal-200 text-teal-800" : "bg-blue-50 border border-blue-200 text-blue-800"}`}>
              {emailSent ? (
                <>{lang === "en" ? "If this email is registered, a reset link has been sent." : "Si cette adresse est enregistrée, un lien de réinitialisation a été envoyé."}</>
              ) : (
                <>
                  {lang === "en"
                    ? "Your request has been logged. An administrator of your organization will contact you shortly with a new password."
                    : "Ta demande a été enregistrée. Un administrateur de ton entreprise va te contacter prochainement avec un nouveau mot de passe."}
                </>
              )}
            </div>
            <Link to="/login" className="block">
              <Button variant="outline" className="w-full gap-1.5" data-testid="back-to-login-btn">
                <ArrowLeft size={14} /> {lang === "en" ? "Back to login" : "Retour à la connexion"}
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-zinc-800 mb-1">{lang === "en" ? "Forgot password" : "Mot de passe oublié"}</h2>
              <p className="text-sm text-zinc-500">{lang === "en" ? "Enter your email and an administrator will help you regain access." : "Entre ton courriel et un administrateur va t'aider à retrouver l'accès."}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fp-email" className="text-zinc-700 font-medium">{lang === "en" ? "Email" : "Courriel"}</Label>
              <div className="relative">
                <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.com" required className="h-12 pl-10 rounded-lg" data-testid="forgot-password-email" />
              </div>
            </div>
            <Button type="submit" disabled={loading || !email} className="w-full h-12 rounded-lg bg-[#002FA7] hover:bg-[#002280] text-white font-semibold" data-testid="forgot-password-submit">
              {loading ? <SpinnerGap className="animate-spin" size={20} /> : (lang === "en" ? "Send request" : "Envoyer la demande")}
            </Button>
            <Link to="/login" className="block text-center text-xs text-zinc-500 hover:text-zinc-700">
              ← {lang === "en" ? "Back to login" : "Retour à la connexion"}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
